import { useCallback, useMemo, useState } from 'react';
import type { EntityDashboardProvider, EntityDashboardProviderData } from '@trinity/chat/types/entity-pages';
import { EntityDashboardProviderDataSchema } from '@trinity/chat/types/entity-pages';
import Toast from '../Toast';
import { DisplayrDashboardModal } from './DisplayrDashboardModal';
import { SelectDashboardModal } from './SelectDashboardModal';
import type { SigmaDashboardFormValues } from './SigmaDashboardModal';
import { SigmaDashboardModal } from './SigmaDashboardModal';
import { TableauDashboardModal } from './TableauDashboardModal';

export type DashboardPageInput = { name: string; providerData: EntityDashboardProviderData };

type Props = {
  open: boolean;
  onClose: () => void;
  /** The dashboard being edited. Omit it to create one: the user picks a provider first. */
  dashboard?: DashboardPageInput;
  /** Persists the dashboard; the caller curries in where it goes and refreshes its own queries. */
  onSave: (input: DashboardPageInput) => Promise<unknown>;
};

const PROVIDERS: readonly EntityDashboardProvider[] = ['displayr', 'sigma', 'tableau'];
const isProvider = (value: string): value is EntityDashboardProvider =>
  (PROVIDERS as readonly string[]).includes(value);

/**
 * Create or edit a project / brand dashboard page: the provider picker (create only), then the
 * provider's own form. Validates against the shared provider schema before saving, and reports
 * failures itself; the modal closes only once the save succeeds.
 */
export const DashboardPageModal = ({ open, onClose, dashboard, onSave }: Props) => {
  const [picked, setPicked] = useState<EntityDashboardProvider | null>(null);
  const provider = dashboard?.providerData.provider ?? picked;
  const isEdit = dashboard !== undefined;

  const handleClose = useCallback(() => {
    setPicked(null);
    onClose();
  }, [onClose]);

  const save = useCallback(
    async (name: string, providerData: EntityDashboardProviderData): Promise<void> => {
      const parsed = EntityDashboardProviderDataSchema.safeParse(providerData);
      if (!parsed.success) {
        Toast.error({ title: 'Dashboard not saved', body: 'Check the dashboard details and try again.' });
        throw parsed.error;
      }
      try {
        await onSave({ name: name.trim(), providerData: parsed.data });
      } catch (error) {
        Toast.error({ title: 'Dashboard not saved', body: 'Something went wrong. Try saving again.' });
        throw error;
      }
      Toast.success({ title: isEdit ? 'Dashboard updated' : 'Dashboard created' });
      setPicked(null);
    },
    [isEdit, onSave]
  );

  const sigmaInitialValues: Partial<SigmaDashboardFormValues> | undefined = useMemo(() => {
    if (dashboard?.providerData.provider !== 'sigma') return undefined;
    const { provider: _provider, analysisId, ...flags } = dashboard.providerData;
    return { ...flags, analysisId: analysisId ?? '', tabName: dashboard.name };
  }, [dashboard]);

  if (!open) return null;

  if (provider === null) {
    return (
      <SelectDashboardModal
        open
        onClose={handleClose}
        onContinue={(selected) => {
          if (isProvider(selected)) setPicked(selected);
        }}
      />
    );
  }

  const initialName = dashboard?.name ?? '';

  if (provider === 'displayr') {
    return (
      <DisplayrDashboardModal
        open
        onClose={handleClose}
        title={isEdit ? 'Edit Displayr Dashboard' : undefined}
        initialDashboardId={
          dashboard?.providerData.provider === 'displayr' ? dashboard.providerData.dashboardId : ''
        }
        initialTabName={initialName}
        updateDisplayrDashboard={({ dashboardId, tabName }) =>
          save(tabName, { provider: 'displayr', dashboardId: dashboardId.trim() })
        }
      />
    );
  }

  if (provider === 'tableau') {
    return (
      <TableauDashboardModal
        open
        onClose={handleClose}
        title={isEdit ? 'Edit Tableau Dashboard' : undefined}
        initialDashboardId={dashboard?.providerData.provider === 'tableau' ? dashboard.providerData.url : ''}
        initialTabName={initialName}
        updateTableauDashboard={({ dashboardId, tabName }) =>
          save(tabName, { provider: 'tableau', url: dashboardId.trim() })
        }
      />
    );
  }

  return (
    <SigmaDashboardModal
      open
      onClose={handleClose}
      title={isEdit ? 'Edit Sigma Dashboard' : undefined}
      initialValues={sigmaInitialValues}
      updateSigmaDashboard={({ tabName, warehouse, schema, analysisId, ...flags }) =>
        save(tabName, {
          provider: 'sigma',
          warehouse: warehouse.trim(),
          schema: schema.trim(),
          analysisId: analysisId?.trim() || undefined,
          includeTreatmentClaimCount: flags.includeTreatmentClaimCount ?? false,
          includeEpidemiology: flags.includeEpidemiology ?? false,
          includeLineOfTherapy: flags.includeLineOfTherapy ?? false,
          includeCompliance: flags.includeCompliance ?? false,
          includePersistence: flags.includePersistence ?? false,
        })
      }
    />
  );
};
